import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileDown, FileText, Download, Upload, Truck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { API } from "@/utils/constants";

export const ImportExportModal = ({ open, onClose, onUpdate, stats }) => {
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const vehicleTypes = [
    { id: "tractor", label: "Tractors", count: stats.tractors, color: "#007AFF", bg: "rgba(0, 122, 255, 0.1)" },
    { id: "chassis", label: "Chassis", count: stats.chassis, color: "#34C759", bg: "rgba(52, 199, 89, 0.1)" },
    { id: "yard_equipment", label: "Yard Equipment", count: stats.yard_equipment, color: "#FF9500", bg: "rgba(255, 149, 0, 0.1)" },
  ];

  const handleDownloadTemplate = (type) => {
    window.open(`${API}/vehicles/template/${type}`, '_blank');
  };

  const handleExport = (type) => {
    window.open(`${API}/vehicles/export/${type}`, '_blank');
  };

  const handleImport = async (type, file) => {
    if (!file) return;
    
    setImporting(true);
    setImportResult(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/vehicles/import/${type}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult({
        success: true,
        message: response.data.message,
        errors: response.data.errors
      });
      if (response.data.imported_count > 0) {
        toast.success(`${response.data.imported_count} vehicles imported successfully`);
        onUpdate();
      }
    } catch (error) {
      setImportResult({
        success: false,
        message: error.response?.data?.detail || "Import failed",
        errors: []
      });
      toast.error("Import failed");
    }
    setImporting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileDown className="w-5 h-5" />
            Import / Export Vehicles
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <p className="text-sm text-slate-500">
            Download templates, export existing vehicles, or import new vehicles using CSV files. 
            Each vehicle type has its own spreadsheet.
          </p>

          {/* Vehicle Type Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {vehicleTypes.map((type) => (
              <div 
                key={type.id}
                className="border rounded-xl p-4 hover:border-blue-300 transition-colors"
                data-testid={`import-export-${type.id}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: type.bg }}
                  >
                    <Truck className="w-5 h-5" style={{ color: type.color }} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{type.label}</h3>
                    <p className="text-sm text-slate-500">{type.count} vehicles</p>
                  </div>
                </div>

                <div className="space-y-2">
                  {/* Download Template */}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full justify-start"
                    onClick={() => handleDownloadTemplate(type.id)}
                    data-testid={`btn-template-${type.id}`}
                  >
                    <FileText className="w-4 h-4 mr-2 text-slate-500" />
                    Download Template
                  </Button>

                  {/* Export */}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full justify-start"
                    onClick={() => handleExport(type.id)}
                    disabled={type.count === 0}
                    data-testid={`btn-export-${type.id}`}
                  >
                    <Download className="w-4 h-4 mr-2 text-green-600" />
                    Export {type.label}
                  </Button>

                  {/* Import */}
                  <div className="relative">
                    <input
                      type="file"
                      accept=".csv"
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      onChange={(e) => handleImport(type.id, e.target.files?.[0])}
                      disabled={importing}
                      data-testid={`input-import-${type.id}`}
                    />
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full justify-start pointer-events-none"
                      disabled={importing}
                    >
                      <Upload className="w-4 h-4 mr-2 text-blue-600" />
                      {importing ? "Importing..." : `Import ${type.label}`}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Import Result */}
          {importResult && (
            <div className={`p-4 rounded-lg ${importResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className={`font-medium ${importResult.success ? 'text-green-800' : 'text-red-800'}`}>
                {importResult.message}
              </div>
              {importResult.errors && importResult.errors.length > 0 && (
                <div className="mt-2 text-sm text-red-600">
                  <p className="font-medium">Errors:</p>
                  <ul className="list-disc list-inside mt-1 max-h-32 overflow-y-auto">
                    {importResult.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* CSV Format Info */}
          <div className="bg-slate-50 rounded-lg p-4">
            <h4 className="font-medium text-slate-900 mb-2">CSV Format</h4>
            <p className="text-sm text-slate-600 mb-2">Required columns: unit_number, vin</p>
            <code className="text-xs text-slate-500 block overflow-x-auto">
              unit_number, year, make, model, vin, vehicle_type, current_mileage, engine_year, engine_make, engine_model, fuel_type, def_equipped
            </code>
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
